import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Form, Button, Row, Col } from 'react-bootstrap';
import Pager from './Pager';
import API from '../services/api';
import { dateTime, fieldLabel, displayValue, cleanIp } from './adminShared';
import { reportName } from '../utils/fileName';

const CATEGORY_LABEL = {
  auth: 'Sesiones', invoices: 'Facturas', quotes: 'Cotizaciones', clients: 'Clientes', products: 'Productos',
  categories: 'Categorías', 'client-prices': 'Precios especiales', payments: 'Pagos', expenses: 'Gastos',
  users: 'Usuarios', admin: 'Administración', account: 'Cuenta', 'rnc-lookup': 'Padrón RNC', audit: 'Auditoría',
};

const emptyFilters = { user: '', category: '', ok: '', from: '', to: '', q: '' };

const AdminActivityTab = () => {
  const [filters, setFilters] = useState(emptyFilters);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, pages: 1 });
  const [meta, setMeta] = useState({ usernames: [], categories: [] });
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { API.get('/admin/activity/meta').then(r => setMeta(r.data)).catch(() => {}); }, []);

  // Free-text search waits for a pause in typing instead of querying per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setFilters(f => (f.q === search ? f : { ...f, q: search })); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const params = useCallback(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), [filters]);

  const load = useCallback(() => {
    API.get('/admin/activity', { params: { ...params(), page, limit: 25 } })
      .then(r => { setData(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la actividad.'));
  }, [params, page]);

  // Live: refresh every 30 s so new activity shows up on its own.
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const change = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };

  const exportCsv = async () => {
    const res = await API.get('/admin/activity/export', { params: params(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName('Actividad')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    load(); // the export itself is now in the log
  };

  return (
    <Card className="shadow-sm p-3">
      <Row className="g-2 mb-3 align-items-end">
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Usuario</Form.Label>
          <Form.Select size="sm" value={filters.user} onChange={e => change('user', e.target.value)}>
            <option value="">Todos</option>
            {meta.usernames.map(u => <option key={u} value={u}>{u}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Área</Form.Label>
          <Form.Select size="sm" value={filters.category} onChange={e => change('category', e.target.value)}>
            <option value="">Todas</option>
            {meta.categories.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Resultado</Form.Label>
          <Form.Select size="sm" value={filters.ok} onChange={e => change('ok', e.target.value)}>
            <option value="">Todos</option>
            <option value="true">Correcto</option>
            <option value="false">Fallido</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Desde</Form.Label>
          <Form.Control size="sm" type="date" value={filters.from} onChange={e => change('from', e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
          <Form.Control size="sm" type="date" value={filters.to} onChange={e => change('to', e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Buscar</Form.Label>
          <Form.Control size="sm" placeholder="factura, cliente, usuario..." value={search} onChange={e => setSearch(e.target.value)} />
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted small">
          {data.total} evento(s){Object.values(filters).some(Boolean) && (
            <> · <Button variant="link" size="sm" className="p-0" onClick={() => { setFilters(emptyFilters); setSearch(''); setPage(1); }}>Quitar filtros</Button></>
          )}
        </span>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={load}><i className="bi bi-arrow-clockwise" /> Actualizar</Button>
          <Button size="sm" variant="outline-primary" onClick={exportCsv}><i className="bi bi-download" /> Exportar CSV</Button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <Table hover responsive className="mb-0 align-middle">
        <thead>
          <tr><th style={{ width: 150 }}>Fecha y hora</th><th style={{ width: 130 }}>Usuario</th><th>Actividad</th><th style={{ width: 90 }}>Resultado</th><th style={{ width: 110 }}>IP</th></tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <React.Fragment key={r._id}>
              <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === r._id ? null : r._id)}>
                <td className="small">{dateTime(r.at)}</td>
                <td>
                  {r.username || <span className="text-muted">—</span>}
                  {r.role && <Badge bg={r.role === 'admin' ? 'warning' : 'secondary'} text={r.role === 'admin' ? 'dark' : undefined} className="ms-1">{r.role}</Badge>}
                </td>
                <td>
                  {r.summary}
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{r.action}</div>
                </td>
                <td><Badge bg={r.ok ? 'success' : 'danger'}>{r.ok ? 'OK' : 'Falló'}</Badge></td>
                <td className="small text-muted">{cleanIp(r.ip)}</td>
              </tr>
              {open === r._id && (
                <tr>
                  <td colSpan={5} style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="small">
                      <div><strong>{r.method}</strong> {r.path} → HTTP {r.statusCode}</div>
                      {r.entityLabel && <div>Elemento: {r.entityLabel} {r.entityId && <span className="text-muted">({r.entityId})</span>}</div>}
                      {r.details?.changes?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-muted mb-1">Qué cambió</div>
                          <Table size="sm" bordered className="mb-2" style={{ maxWidth: 760 }}>
                            <thead><tr><th style={{ width: '24%' }}>Campo</th><th>Antes</th><th>Después</th></tr></thead>
                            <tbody>
                              {r.details.changes.map((c, i) => (
                                <tr key={i}>
                                  <td>{fieldLabel(c.field)}</td>
                                  <td style={{ color: '#F87171' }}>{displayValue(c.from)}</td>
                                  <td style={{ color: '#4ADE80' }}>{displayValue(c.to)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {['created', 'deleted'].map(kind => r.details?.[kind]?.length > 0 && (
                        <div className="mt-2" key={kind}>
                          <div className="text-muted mb-1">{kind === 'created' ? 'Contenido del documento creado' : 'Contenido que se eliminó'}</div>
                          <Table size="sm" bordered className="mb-2" style={{ maxWidth: 760 }}>
                            <tbody>
                              {r.details[kind].map((f, i) => (
                                <tr key={i}><td style={{ width: '30%' }}>{fieldLabel(f.field)}</td><td>{displayValue(f.value)}</td></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ))}
                      {(r.details?.body || r.details?.result) && (
                        <details className="mt-1">
                          <summary className="text-muted" style={{ cursor: 'pointer' }}>Datos enviados / respuesta</summary>
                          <pre className="mb-1 mt-1" style={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>{JSON.stringify({ body: r.details.body, result: r.details.result }, null, 2)}</pre>
                        </details>
                      )}
                      <div className="text-muted">{r.userAgent}</div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {data.rows.length === 0 && <tr><td colSpan={5} className="text-center text-muted">Sin actividad para estos filtros.</td></tr>}
        </tbody>
      </Table>

      {data.pages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">Página {data.page} de {data.pages}</span>
          <Pager page={page} pages={data.pages} onChange={setPage} showEnds />
        </div>
      )}
    </Card>
  );
};

export default AdminActivityTab;
